import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendMessage, fetchOrderDetails, fetchUsername, connectAgent } from '../api/chatApi';
import { useIdleTimeout } from './useIdleTimeout';
import {
  addMessage as addMessageAction,
  setEscalationRequired,
  setInitialOrderNumber,
  setIsAnalyzing,
  setIsLiveAgent,
  setLiveAgentConnectivity,
  setLoading,
  setOrder,
  setOrderLoading,
  setSessionEnded,
  setStreamingEvents,
  setUserName,
  setUserNameLoading,
} from '../redux/chatSlice';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Parse session and uid from the URL query params: ?session=<id>&uid=<id>
 * Only allows access via the /userchat path.
 */
function getParamsFromUrl() {
  if (!window.location.pathname.endsWith('/userchat')) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session');
  const uuid = params.get('uid');
  if (sessionId && uuid) {
    return { sessionId, uuid };
  }
  return null;
}

/**
 * Normalize the /chat GET response into the flat shape OrderCard expects.
 * The backend nests order fields under `order_details` (and the delivery
 * date under the first line item), while OrderCard reads flat top-level
 * fields such as order.order_number / order.order_status.
 */
function normalizeOrderResponse(data) {
  const details = data?.order_details || {};
  const firstLine = Array.isArray(details.lines) ? details.lines[0] : undefined;

  return {
    ...details,
    CaseID: data?.CaseID,
    intent: data?.intent,
    message: typeof details.message === 'string' && details.message.trim() ? details.message : data?.message,
    starter_message: data?.starter_message,
    estimated_delivery_date: details.estimated_delivery_date || firstLine?.estimated_delivery_date,
  };
}

/**
 * Custom hook encapsulating all chat state and logic.
 * Keeps components lean and makes the chat logic reusable/testable.
 */
export function useChat() {
  const [params] = useState(getParamsFromUrl);
  const sessionId = params?.sessionId;
  const uuid = params?.uuid;
  const dispatch = useDispatch();
  const {
    messages,
    loading,
    isAnalyzing,
    streamingEvents,
    isLiveAgent,
    order,
    initialOrderNumber,
    orderLoading,
    userName,
    userNameLoading,
    sessionEnded,
    liveAgentConnectivity,
    escalationRequired,
  } = useSelector((state) => state.chat);

  // Dummy predefined questions — replace with API call when endpoint is ready
  const suggestedQuestions = [
    { id: 1, text: 'What is the status of my order?' },
    { id: 2, text: 'When will my order be delivered?' },
    { id: 3, text: 'Can I modify my order?' },
    { id: 4, text: 'I need help with a return or refund.' },
    { id: 5, text: 'Who is the point of contact for my order?' },
  ];

  const handleSessionTimeout = useCallback(() => {
    if (!sessionId || !uuid) return;
    sendMessage(sessionId, uuid, '', userName, 'true')
      .then((answer) => console.log('Session timeout /question response:', answer))
      .catch((err) => console.error('Failed to notify session timeout:', err));
  }, [sessionId, uuid, userName]);

  const { isIdle: sessionTimedOut } = useIdleTimeout(SESSION_TIMEOUT_MS, handleSessionTimeout);

  const hasFetched = useRef(false);

  // Fetch order details and username when the chat page loads
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    if (!sessionId || !uuid) {
      console.warn('No session_id/uuid found in URL. Expected: /chat/<session_id>/<uuid>');
      dispatch(setOrderLoading(false));
      dispatch(setUserNameLoading(false));
      return;
    }

    fetchOrderDetails(sessionId, uuid)
      .then((data) => {
        const normalizedOrder = normalizeOrderResponse(data);
        dispatch(setOrder(normalizedOrder));
        if (normalizedOrder.order_number) {
          dispatch(setInitialOrderNumber(String(normalizedOrder.order_number).trim()));
        }
        if (normalizedOrder.message) {
          dispatch(addMessageAction({ role: 'assistant', content: normalizedOrder.message, timestamp: Date.now(), agentType: 'ai' }));
        } else if (data.starter_message) {
          dispatch(addMessageAction({ role: 'assistant', content: data.starter_message, timestamp: Date.now(), agentType: 'ai' }));
        }
      })
      .catch((err) => console.error('Failed to fetch order details:', err))
      .finally(() => dispatch(setOrderLoading(false)));

    fetchUsername(sessionId, uuid)
      .then((data) => {
        dispatch(setUserName(data.customerEmailId || ''));

        // live_agent_connectivity auto-trigger disabled for testing.
        if (data.live_agent_connectivity === true) {
          dispatch(setLiveAgentConnectivity(true));
        }
      })
      .catch((err) => console.error('Failed to fetch username:', err))
      .finally(() => dispatch(setUserNameLoading(false)));
  }, [sessionId, uuid]);

  const addMessage = useCallback((msg) => {
    dispatch(addMessageAction(msg));
  }, [dispatch]);

  // Reveal the backend's streaming_events (planning/node_executed/complete)
  // one at a time: each new event replaces the previously displayed one in
  // the UI, replaying the gaps between each event's timestamp_ms so the
  // pacing mirrors how long each step actually took on the backend.
  // The real backend gaps are scaled down (STREAMING_SPEED_FACTOR) and capped
  // (STREAMING_MAX_DELAY_MS) so the UI moves through events noticeably faster.
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const STREAMING_SPEED_FACTOR = 0.35;
  const STREAMING_MAX_DELAY_MS = 700;

  const playStreamingEvents = useCallback(async (events) => {
    if (!Array.isArray(events) || events.length === 0) return;

    let previousTime = 0;
    for (const event of events) {
      if (event?.message) {
        dispatch(setStreamingEvents([event]));
        const rawDelay = Math.max(0, (event.timestamp_ms ?? previousTime) - previousTime);
        const delay = Math.min(rawDelay * STREAMING_SPEED_FACTOR, STREAMING_MAX_DELAY_MS);
        await sleep(delay);
        previousTime = event.timestamp_ms ?? previousTime;
      }
    }
  }, [dispatch]);

  const send = useCallback(
    async (text) => {
      const trimmed = text.trim();
      // Live agent hand-off is active for this session — block the /question
      // chat flow entirely (the UI also disables the input in this state).
      if (!trimmed || loading || liveAgentConnectivity || sessionTimedOut) return;

      dispatch(setSessionEnded(false));
      addMessage({ role: 'user', content: trimmed, timestamp: Date.now() });
      dispatch(setStreamingEvents([]));
      dispatch(setIsAnalyzing(true));
      dispatch(setLoading(true));

      try {
        const answer = await sendMessage(sessionId, uuid, trimmed, userName, 'false');
        dispatch(setIsAnalyzing(false));

        if (answer && typeof answer === 'object' && Array.isArray(answer.streamingEvents)) {
          await playStreamingEvents(answer.streamingEvents);
        }

        if (answer && typeof answer === 'object' && Array.isArray(answer.liveAgentMessages)) {
          const firstMessage = answer.liveAgentMessages[0];
          const secondMessage = answer.liveAgentMessages[1];
          dispatch(setIsLiveAgent(true));

          if (firstMessage) {
            addMessage({
              role: 'assistant',
              content: firstMessage,
              timestamp: Date.now(),
              agentType: 'ai',
            });
          }

          if (secondMessage) {
            setTimeout(() => {
              addMessage({
                role: 'assistant',
                content: secondMessage,
                timestamp: Date.now(),
                agentType: 'live',
              });
            }, 2000);
          }
        } else if (answer && typeof answer === 'object' && answer.orderCard) {
          const responseOrderNumber = String(answer.orderCard.order_number || '').trim();
          const isSameOrder = initialOrderNumber && responseOrderNumber && responseOrderNumber === initialOrderNumber;
          const hasTableInResponse = /<table[\s\S]*?<\/table>/i.test(answer.content || '');
          addMessage({
            role: 'assistant',
            content: answer.content || '',
            orderCard: (isSameOrder || hasTableInResponse) ? undefined : answer.orderCard,
            intent: answer.intent || '',
            proactiveMessage: answer.proactiveMessage || '',
            tableOrderNumber: answer.tableOrderNumber || '',
            agentType: answer.isAgentMessage ? 'live' : 'ai',
            timestamp: Date.now(),
          });
        } else if (answer && typeof answer === 'object' && answer.tableOrderNumber) {
          addMessage({
            role: 'assistant',
            content: answer.content || '',
            intent: answer.intent || '',
            tableOrderNumber: answer.tableOrderNumber,
            agentType: answer.isAgentMessage ? 'live' : 'ai',
            timestamp: Date.now(),
          });
        } else if (answer && typeof answer === 'object' && answer.isAgentMessage) {
          addMessage({
            role: 'assistant',
            content: answer.content || '',
            agentType: 'live',
            timestamp: Date.now(),
          });
        } else if (answer && typeof answer === 'object' && answer.sessionEndMessage) {
          addMessage({
            role: 'assistant',
            content: answer.sessionEndMessage,
            agentType: isLiveAgent ? 'live' : 'ai',
            timestamp: Date.now(),
          });
          dispatch(setSessionEnded(true));
        } else if (answer && typeof answer === 'object') {
          addMessage({
            role: 'assistant',
            content: answer.content || 'Sorry, something went wrong.',
            agentType: 'ai',
            timestamp: Date.now(),
          });
        } else {
          addMessage({
            role: 'assistant',
            content: answer || 'Sorry, something went wrong.',
            agentType: 'ai',
            timestamp: Date.now(),
          });
        }

        // Trigger the live-agent handoff only after the answer/streaming events
        // above have been added, so the transfer notice never renders before
        // the answer it's escalating from.
        if (answer && typeof answer === 'object' && answer.escalationRequired) {
          dispatch(setEscalationRequired(true));
        }
      } catch (err) {
        const errorContent =
          err.message === 'Failed to fetch'
            ? 'Cannot reach the backend. Is it running?'
            : err.message || 'Sorry, something went wrong. Please try again.';

        addMessage({
          role: 'assistant',
          content: `⚠️ ${errorContent}`,
          agentType: 'ai',
          timestamp: Date.now(),
        });
      } finally {
        dispatch(setIsAnalyzing(false));
        dispatch(setStreamingEvents([]));
        dispatch(setLoading(false));
      }
    },
    [sessionId, uuid, loading, liveAgentConnectivity, sessionTimedOut, addMessage, initialOrderNumber, userName, playStreamingEvents, dispatch, isLiveAgent]
  );

  // Consumed by the UI right after it acts on an escalation, so a later
  // /question response can set escalation_required === true again and
  // re-trigger the live-agent handoff (state setters no-op on an unchanged
  // value, so leaving this stuck at true would block subsequent escalations).
  const clearEscalation = useCallback(() => {
    dispatch(setEscalationRequired(false));
  }, [dispatch]);

  // Called when the user exits the live-agent chat, so `send()` no longer
  // blocks the /question flow and the session doesn't immediately re-trigger
  // the DirectLine handoff it was auto-connected for.
  const clearLiveAgentConnectivity = useCallback(() => {
    dispatch(setLiveAgentConnectivity(false));
  }, [dispatch]);

  const switchToLiveAgent = useCallback(async () => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    const recentQuestion = lastUserMsg?.content || '';

    try {
      const data = await connectAgent(sessionId, uuid, recentQuestion);
      addMessage({
        role: 'assistant',
        content: data.message || 'Connecting to live agent…',
        agentType: 'live',
        timestamp: Date.now(),
      });
      dispatch(setIsLiveAgent(true));
    } catch (err) {
      addMessage({
        role: 'assistant',
        content: '⚠️ Unable to connect to a live agent. Please try again.',
        agentType: 'ai',
        timestamp: Date.now(),
      });
    }
  }, [sessionId, uuid, messages, addMessage, dispatch]);

  return { messages, loading, isAnalyzing, streamingEvents, send, order, orderLoading, userName, userNameLoading, suggestedQuestions, sessionEnded, switchToLiveAgent, liveAgentConnectivity, isLiveAgent, escalationRequired, clearEscalation, clearLiveAgentConnectivity, sessionTimedOut, sessionId, uuid };
}
